import { useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrderDetailsResponse, OrdersSummary } from '../api/types';
import { formatCurrency, formatDateTime, formatStatus } from '../utils/format';
import { useSession } from '../state/SessionContext';
import './DashboardPage.css';

// Transaction type colors mapping
const TRANSACTION_TYPE_COLORS: Record<string, string> = {
  withdrawal: '#f97316',
  'auto-withdrawal': '#8b5cf6',
  raffle: '#ec4899',
  raffle_create: '#ec4899',
  raffle_win: '#10b981',
  raffle_refund: '#f59e0b',
  correction: '#6366f1',
  refund: '#f59e0b',
};

const DEFAULT_TYPE_COLOR = '#3b82f6'; // Blue for deposits and unknown types

// Transaction type labels mapping
const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  withdrawal: 'Withdrawal',
  'auto-withdrawal': 'Auto-payout',
  raffle: 'Raffle',
  raffle_create: 'Raffle Reserve',
  raffle_win: 'Raffle Win',
  raffle_refund: 'Raffle Refund',
  correction: 'Correction',
  refund: 'Refund',
};

const DEFAULT_TYPE_LABEL = 'Deposit';

interface BalanceData {
  balanceUsdt: number;
  totalEarnedUsdt: number;
  totalWithdrawnUsdt: number;
}

interface TransactionItem {
  id: string;
  type: 'payment' | 'withdrawal' | 'auto-withdrawal' | 'raffle';
  orderId?: string;
  status: string;
  currency?: string;
  amount: number;
  createdAt: string;
  expiresAt?: string | null;
  comment?: string | null;
  withdrawalAddress?: string;
  merchantAddress?: string;
  txId?: string | null;
  sentAt?: string | null;
  lastError?: string | null;
  attempts?: number;
  raffleId?: string;
  scheduledAt?: string;
  participantCount?: number;
  winnerUsername?: string | null;
  // Payer information
  isAnonymous?: boolean;
  payerUsername?: string | null;
  payerFirstName?: string | null;
  payerTelegramId?: string | null;
  payerEmail?: string | null;
  payerPhone?: string | null;
}

type SummaryState =
  | { kind: 'loading' }
  | { kind: 'loaded'; summary: OrdersSummary; latest: TransactionItem[]; balance: BalanceData }
  | { kind: 'error'; message: string };

const statusColors: Record<string, string> = {
  waiting: 'status-badge waiting',
  active: 'status-badge waiting', // Same as waiting for raffles
  completed: 'status-badge completed',
  cancelled: 'status-badge expired', // Orange for cancelled
  failed: 'status-badge failed',
  expired: 'status-badge expired',
};

const ORDERS_PER_PAGE = 20;

function DashboardPage() {
  const { user } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<SummaryState>({ kind: 'loading' });
  const [currentPage, setCurrentPage] = useState(1);

  // Initialize filters from URL parameters
  const selectedStatus = searchParams.get('status') || '';
  const selectedCurrency = searchParams.get('currency') || '';
  const selectedType = searchParams.get('type') || 'all';

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }

    const fullUrl = `${window.location.pathname}?${newParams.toString()}`;
    console.log('=== FILTER UPDATE ===');
    console.log(`Filter changed: ${key} = "${value || 'empty'}"`);
    console.log(`Full redirect URL: ${fullUrl}`);
    console.log('URL params:', Object.fromEntries(newParams));
    console.log('========================');

    setSearchParams(newParams);
  };

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [withdrawAddress, setWithdrawAddress] = useState<string>('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string>('');
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [savedWithdrawalAddresses, setSavedWithdrawalAddresses] = useState<string[]>([]);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse | null>(null);
  const [detailsOrderId, setDetailsOrderId] = useState<string | null>(null);
  const [showLedgerDetailsModal, setShowLedgerDetailsModal] = useState(false);
  const [ledgerDetails, setLedgerDetails] = useState<TransactionItem | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchData() {
      try {
        const offset = (currentPage - 1) * ORDERS_PER_PAGE;

        // Decide which API to call based on selected type
        let items: TransactionItem[] = [];

        if (selectedType === 'all') {
          // Raffle-specific statuses that don't exist in orders/withdrawals
          const RAFFLE_ONLY_STATUSES = ['active', 'cancelled'];
          const isRaffleStatus = RAFFLE_ONLY_STATUSES.includes(selectedStatus);

          // Load deposits, withdrawals, auto-withdrawals, balance transactions + raffles if needed
          const [ordersResponse, withdrawalsResponse, merchantPayoutsResponse, balanceHistoryResponse, rafflesResponse] = await Promise.all([
            api.getOrders(ORDERS_PER_PAGE * 2, 0, selectedStatus || undefined, selectedCurrency || undefined),
            api.getWithdrawals(ORDERS_PER_PAGE * 2, 0),
            api.getMerchantPayouts(ORDERS_PER_PAGE * 2),
            api.getBalanceHistory(ORDERS_PER_PAGE * 2, 0),
            isRaffleStatus ? api.getMyRaffles() : Promise.resolve({ raffles: [] }),
          ]);

          let depositItems = ordersResponse.items.map(order => ({
            id: order.orderId,
            type: 'payment' as const,
            orderId: order.orderId,
            status: order.status,
            currency: order.currency,
            amount: order.usdtAmount,
            createdAt: order.createdAt,
            expiresAt: order.expiresAt,
            comment: order.comment,
            isAnonymous: order.isAnonymous,
            payerUsername: order.payerUsername,
            payerFirstName: order.payerFirstName,
            payerTelegramId: order.payerTelegramId,
            payerEmail: order.payerEmail,
            payerPhone: order.payerPhone,
          }));

          let withdrawalItems = withdrawalsResponse.withdrawals.map(wd => ({
            id: wd.id,
            type: 'withdrawal' as const,
            status: wd.status,
            amount: wd.amountUsdt,
            createdAt: wd.createdAt,
            withdrawalAddress: wd.withdrawalAddress,
            txId: wd.txId,
          }));

          let autoWithdrawalItems = merchantPayoutsResponse.payouts.map(mp => ({
            id: mp.id,
            type: 'auto-withdrawal' as const,
            orderId: mp.orderId,
            status: mp.status,
            amount: mp.amountUsdt,
            createdAt: mp.createdAt,
            merchantAddress: mp.merchantAddress,
            txId: mp.txId,
            sentAt: mp.sentAt,
            lastError: mp.lastError,
            attempts: mp.attempts,
          }));

          // Map balance history transactions (raffles, refunds, corrections)
          let balanceTransactionItems = balanceHistoryResponse.transactions
            .filter(tx => tx.type !== 'deposit') // Skip deposit type as it's already in Orders
            .map(tx => ({
              id: tx.id,
              type: tx.type as 'raffle_create' | 'raffle_win' | 'raffle_refund' | 'correction' | 'refund',
              status: 'completed' as const, // Balance transactions are always completed
              amount: Math.abs(tx.amount), // Show positive amount
              createdAt: tx.createdAt,
              description: tx.description,
              orderId: tx.orderId,
            }));

          let raffleItems = rafflesResponse.raffles.map((raffle: any) => ({
            id: raffle.id,
            type: 'raffle' as const,
            raffleId: raffle.raffleId,
            status: raffle.status,
            amount: raffle.prizeAmountUsdt,
            createdAt: raffle.createdAt,
            scheduledAt: raffle.scheduledAt,
            participantCount: raffle.participantCount,
            winnerUsername: raffle.winnerUsername,
          }));

          // Apply client-side filtering
          if (selectedStatus) {
            depositItems = depositItems.filter(item => item.status === selectedStatus);
            withdrawalItems = withdrawalItems.filter(item => item.status === selectedStatus);
            autoWithdrawalItems = autoWithdrawalItems.filter(item => item.status === selectedStatus);
            raffleItems = raffleItems.filter(item => item.status === selectedStatus);
            // Balance transactions are always 'completed', so filter accordingly
            if (selectedStatus !== 'completed') {
              balanceTransactionItems = [];
            }
          }
          if (selectedCurrency) {
            depositItems = depositItems.filter(item => item.currency === selectedCurrency);
            raffleItems = []; // Raffles are always USDT, exclude if specific non-USDT currency selected
            // Balance transactions are USDT only
            if (selectedCurrency !== 'USDT') {
              balanceTransactionItems = [];
            }
          }

          // Merge and sort by date (newest first)
          const allItems = [...depositItems, ...withdrawalItems, ...autoWithdrawalItems, ...balanceTransactionItems, ...raffleItems];
          allItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          // Apply pagination on client side
          items = allItems.slice(offset, offset + ORDERS_PER_PAGE);
        } else if (selectedType === 'auto-withdrawals') {
          // Load auto-withdrawals (merchant payouts) only
          const merchantPayoutsResponse = await api.getMerchantPayouts(ORDERS_PER_PAGE);
          let autoWithdrawalItems = merchantPayoutsResponse.payouts.map(mp => ({
            id: mp.id,
            type: 'auto-withdrawal' as const,
            orderId: mp.orderId,
            status: mp.status,
            amount: mp.amountUsdt,
            createdAt: mp.createdAt,
            merchantAddress: mp.merchantAddress,
            txId: mp.txId,
            sentAt: mp.sentAt,
            lastError: mp.lastError,
            attempts: mp.attempts,
          }));

          if (selectedStatus) {
            autoWithdrawalItems = autoWithdrawalItems.filter(item => item.status === selectedStatus);
          }

          items = autoWithdrawalItems;
        } else if (selectedType === 'deposits') {
          // Call orders endpoint for deposits only
          const orders = await api.getOrders(
            ORDERS_PER_PAGE,
            offset,
            selectedStatus || undefined,
            selectedCurrency || undefined,
          );

          let depositItems = orders.items.map(order => ({
            id: order.orderId,
            type: 'payment' as const,
            orderId: order.orderId,
            status: order.status,
            currency: order.currency,
            amount: order.usdtAmount,
            createdAt: order.createdAt,
            expiresAt: order.expiresAt,
            comment: order.comment,
            isAnonymous: order.isAnonymous,
            payerUsername: order.payerUsername,
            payerFirstName: order.payerFirstName,
            payerTelegramId: order.payerTelegramId,
            payerEmail: order.payerEmail,
            payerPhone: order.payerPhone,
          }));

          // Apply client-side filtering as a fallback if backend ignores filters
          if (selectedStatus) {
            depositItems = depositItems.filter(item => item.status === selectedStatus);
          }
          if (selectedCurrency) {
            depositItems = depositItems.filter(item => item.currency === selectedCurrency);
          }

          items = depositItems;
        } else if (selectedType === 'withdrawals') {
          // Call withdrawals endpoint
          const withdrawalsResponse = await api.getWithdrawals(ORDERS_PER_PAGE, offset);
          let withdrawalItems = withdrawalsResponse.withdrawals.map(wd => ({
            id: wd.id,
            type: 'withdrawal' as const,
            status: wd.status,
            amount: wd.amountUsdt,
            createdAt: wd.createdAt,
            withdrawalAddress: wd.withdrawalAddress,
            txId: wd.txId,
          }));

          if (selectedStatus) {
            withdrawalItems = withdrawalItems.filter(item => item.status === selectedStatus);
          }

          // No currency field on withdrawals; they are USDT by design
          items = withdrawalItems;
        } else if (selectedType === 'raffles') {
          // Call raffles endpoint
          const rafflesResponse = await api.getMyRaffles();
          let raffleItems = rafflesResponse.raffles.map(raffle => ({
            id: raffle.id,
            type: 'raffle' as const,
            raffleId: raffle.raffleId,
            status: raffle.status,
            amount: raffle.prizeAmountUsdt,
            createdAt: raffle.createdAt,
            scheduledAt: raffle.scheduledAt,
            participantCount: raffle.participantCount,
            winnerUsername: raffle.winnerUsername,
          }));

          if (selectedStatus) {
            raffleItems = raffleItems.filter(item => item.status === selectedStatus);
          }

          items = raffleItems;
        }

        const [summary, balance] = await Promise.all([
          api.getOrdersSummary(),
          api.getBalance(),
        ]);

        if (!mounted) {
          return;
        }

        setState({ kind: 'loaded', summary, latest: items, balance });
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        console.error('Selected type:', selectedType);
        if (mounted) {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Failed to load dashboard data',
          });
        }
      }
    }

    void fetchData();

    return () => {
      mounted = false;
    };
  }, [currentPage, selectedStatus, selectedCurrency, selectedType]);

  const openWithdrawalModal = async () => {
    try {
      const result = await api.getSavedWithdrawalAddresses();
      setSavedWithdrawalAddresses(result.addresses);
    } catch (error) {
      console.error('Failed to load saved addresses:', error);
      setSavedWithdrawalAddresses([]);
    }
    setShowWithdrawModal(true);
  };

  const handleWithdrawalSubmit = async () => {
    // Reset previous messages
    setWithdrawError('');
    setWithdrawSuccess(false);

    // Validation
    const amountNum = parseFloat(withdrawAmount);
    if (!withdrawAmount || isNaN(amountNum) || amountNum <= 0) {
      setWithdrawError('Please enter a valid amount greater than 0');
      return;
    }

    if (state.kind !== 'loaded') {
      setWithdrawError('Unable to load balance information');
      return;
    }

    if (amountNum > state.balance.balanceUsdt) {
      setWithdrawError(`Amount exceeds available balance (${state.balance.balanceUsdt.toFixed(2)} USDT)`);
      return;
    }

    if (!withdrawAddress || withdrawAddress.trim().length === 0) {
      setWithdrawError('Please enter a wallet address');
      return;
    }

    // Validate TRON address format (34 chars, starts with T, valid Base58)
    // Base58 = 1-9, A-Z (except O, I), a-z (except l, o)
    const tronAddressRegex = /^T[1-9A-HJ-NP-Za-km-np-z]{33}$/;
    if (!tronAddressRegex.test(withdrawAddress.trim())) {
      setWithdrawError('Invalid TRON address format (must be 34 characters starting with T)');
      return;
    }

    try {
      setWithdrawLoading(true);
      await api.createWithdrawal(amountNum, withdrawAddress.trim());
      setWithdrawSuccess(true);
      setWithdrawAmount('');
      setWithdrawAddress('');

      // Close modal after 2 seconds on success
      setTimeout(() => {
        setShowWithdrawModal(false);
        setWithdrawSuccess(false);
      }, 2000);

      // Refetch balance after successful withdrawal
      void (async () => {
        try {
          const balance = await api.getBalance();
          if (state.kind === 'loaded') {
            setState({ ...state, balance });
          }
        } catch (err) {
          console.error('Failed to refresh balance', err);
        }
      })();
    } catch (err) {
      setWithdrawError(err instanceof Error ? err.message : 'Withdrawal request failed');
      setWithdrawLoading(false);
    }
  };

  const closeWithdrawalModal = () => {
    setShowWithdrawModal(false);
    setWithdrawError('');
    setWithdrawSuccess(false);
    setWithdrawAmount('');
    setWithdrawAddress('');
  };

  const openOrderDetails = async (orderId: string) => {
    setDetailsOrderId(orderId);
    setShowOrderDetailsModal(true);
    setOrderDetails(null);
    setOrderDetailsError(null);
    setOrderDetailsLoading(true);
    try {
      const details = await api.getOrderDetails(orderId);
      setOrderDetails(details);
    } catch (error) {
      setOrderDetailsError(error instanceof Error ? error.message : 'Failed to load order details');
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const retryOrderDetails = () => {
    if (detailsOrderId) {
      void openOrderDetails(detailsOrderId);
    }
  };

  const closeOrderDetails = () => {
    setShowOrderDetailsModal(false);
    setOrderDetails(null);
    setOrderDetailsError(null);
    setOrderDetailsLoading(false);
    setDetailsOrderId(null);
  };

  const handleOrderRowKeyDown = (
    event: KeyboardEvent<HTMLTableRowElement>,
    orderId: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openOrderDetails(orderId);
    }
  };

  const ledgerStatuses: Record<TransactionItem['type'], string[]> = {
    payment: [],
    withdrawal: ['completed'],
    'auto-withdrawal': ['sent', 'failed', 'queued', 'processing'],
    raffle: [],
  };

  const isLedgerClickable = (item: TransactionItem) => {
    const allowed = ledgerStatuses[item.type] ?? [];
    const normalized = (item.status || '').trim().toLowerCase();
    return allowed.length > 0 && allowed.includes(normalized);
  };

  const openLedgerDetails = (item: TransactionItem) => {
    if (!isLedgerClickable(item)) {
      return;
    }
    setLedgerDetails(item);
    setShowLedgerDetailsModal(true);
  };

  const closeLedgerDetails = () => {
    setLedgerDetails(null);
    setShowLedgerDetailsModal(false);
    setRetryError(null);
  };

  const handleRetryPayout = async () => {
    if (!ledgerDetails || ledgerDetails.type !== 'auto-withdrawal') return;

    setRetryLoading(true);
    setRetryError(null);

    try {
      await api.retryMerchantPayout(ledgerDetails.id);
      // Close modal and refresh data
      closeLedgerDetails();
      // Trigger data refresh by resetting state
      setState({ kind: 'loading' });
    } catch (error) {
      setRetryError(error instanceof Error ? error.message : 'Failed to retry payout');
    } finally {
      setRetryLoading(false);
    }
  };

  const handleLedgerRowKeyDown = (
    event: KeyboardEvent<HTMLTableRowElement>,
    item: TransactionItem,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openLedgerDetails(item);
    }
  };

  const getClickableRowProps = (orderId?: string | null) => {
    if (!orderId) {
      return {};
    }
    return {
      className: 'clickable-row',
      onClick: () => openOrderDetails(orderId),
      onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) =>
        handleOrderRowKeyDown(event, orderId),
      tabIndex: 0,
      role: 'button' as const,
    };
  };

  if (state.kind === 'loading') {
    return <div className="card">Loading dashboard…</div>;
  }

  if (state.kind === 'error') {
    return (
      <div className="card error">
        <h2>Dashboard unavailable</h2>
        <p>{state.message}</p>
      </div>
    );
  }

  const { summary, latest, balance } = state;
  const detailsFeeTotalUsdt = orderDetails
    ? orderDetails.serviceFeeUsdt + orderDetails.transferFeeUsdt
    : 0;
  const primaryTxHash =
    orderDetails?.detectedTxId ?? orderDetails?.transactions[0]?.txId ?? null;

  return (
    <div className="dashboard">
      {/* Orders Stats */}
      <div className="metrics-grid">
        <DashboardStat title="Orders total" value={summary.totalOrders.toLocaleString()} />
        <DashboardStat title="Waiting" value={summary.waiting.toLocaleString()} tone="amber" />
        <DashboardStat title="Completed" value={summary.completed.toLocaleString()} tone="green" />
        <DashboardStat title="Expired" value={summary.expired.toLocaleString()} tone="slate" />
      </div>

      <div className="card">
        <h2>Volume by currency</h2>
        <div className="volume-grid">
          {Object.entries(summary.volumeByCurrency).map(([currency, value]) => (
            <div key={currency} className="volume-item">
              <span className="volume-label">{currency}</span>
              <span className="volume-value">
                {value.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                USDT
              </span>
            </div>
          ))}
          {Object.keys(summary.volumeByCurrency).length === 0 && (
            <p className="muted">No volume recorded yet.</p>
          )}
        </div>
      </div>

      {/* User Balance */}
      <div className="card">
        <h2 style={{ margin: '0 0 1.5rem 0' }}>Your Balance</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div
            onClick={() => balance.balanceUsdt > 0 && openWithdrawalModal()}
            style={{
              padding: '1rem',
              backgroundColor: '#1a1f2e',
              borderRadius: '0.5rem',
              cursor: balance.balanceUsdt > 0 ? 'pointer' : 'default',
              opacity: balance.balanceUsdt > 0 ? 1 : 0.6,
              transition: 'all 0.2s ease',
              border: showWithdrawModal ? '2px solid #f97316' : '2px solid transparent',
            }}
            onMouseEnter={(e) => {
              if (balance.balanceUsdt > 0) {
                e.currentTarget.style.backgroundColor = '#252d3d';
                e.currentTarget.style.transform = 'scale(1.02)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#1a1f2e';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <p style={{ margin: '0 0 0.5rem 0', color: '#999', fontSize: '0.9rem' }}>
              {balance.balanceUsdt > 0 ? 'Current Balance (Click to withdraw)' : 'Current Balance'}
            </p>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
              {balance.balanceUsdt.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              USDT
            </p>
          </div>

          <div style={{ padding: '1rem', backgroundColor: '#1a1f2e', borderRadius: '0.5rem' }}>
            <p style={{ margin: '0 0 0.5rem 0', color: '#999', fontSize: '0.9rem' }}>Total Earned</p>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#10b981' }}>
              {balance.totalEarnedUsdt.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              USDT
            </p>
          </div>

          <div style={{ padding: '1rem', backgroundColor: '#1a1f2e', borderRadius: '0.5rem' }}>
            <p style={{ margin: '0 0 0.5rem 0', color: '#999', fontSize: '0.9rem' }}>Total Withdrawn</p>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#f97316' }}>
              {balance.totalWithdrawnUsdt.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              USDT
            </p>
          </div>
        </div>
      </div>

      {/* Personal Card Link */}
      <div className="card personal-card-link">
        <div className="personal-card-content">
          <div className="personal-card-info">
            <h2>My Payment Card</h2>
            <p className="muted">Share your payment link to receive USDT or card transfers.</p>
            {user && (
              <a
                href={`/card/${user.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="personal-card-url"
              >
                {window.location.origin}/card/{user.id}
              </a>
            )}
          </div>
          <a href="/my-card" className="personal-card-btn">
            Edit Card
          </a>
        </div>
      </div>

      {/* All orders with pagination and filters */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>
            {selectedType === 'all' ? 'All Transactions' : selectedType === 'withdrawals' ? 'Withdrawals' : selectedType === 'auto-withdrawals' ? 'Auto-withdrawals' : selectedType === 'raffles' ? 'Raffles' : 'Deposits'}
          </h2>
          <span className="muted">Page {currentPage}</span>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.9rem', color: '#999' }}>Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                updateFilter('status', e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: '#1a1f2e',
                color: '#fff',
                border: '1px solid #333',
                borderRadius: '0.5rem',
                cursor: 'pointer',
              }}
            >
              <option value="">All Statuses</option>
              {selectedType === 'raffles' ? (
                <>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="failed">Failed</option>
                  <option value="expired">Expired</option>
                </>
              ) : selectedType === 'all' ? (
                <>
                  <option value="waiting">Waiting</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="expired">Expired</option>
                  <option value="active">Active (Raffle)</option>
                  <option value="cancelled">Cancelled (Raffle)</option>
                </>
              ) : (
                <>
                  <option value="waiting">Waiting</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="expired">Expired</option>
                </>
              )}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.9rem', color: '#999' }}>Currency</label>
            <select
              value={selectedCurrency}
              onChange={(e) => {
                updateFilter('currency', e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: '#1a1f2e',
                color: '#fff',
                border: '1px solid #333',
                borderRadius: '0.5rem',
                cursor: 'pointer',
              }}
            >
              <option value="">All Currencies</option>
              <option value="BTC">BTC</option>
              <option value="ETH">ETH</option>
              <option value="USDT">USDT</option>
              <option value="TON">TON</option>
              <option value="TRX">TRX</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.9rem', color: '#999' }}>Type</label>
            <select
              value={selectedType}
              onChange={(e) => {
                updateFilter('type', e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: '#1a1f2e',
                color: '#fff',
                border: '1px solid #333',
                borderRadius: '0.5rem',
                cursor: 'pointer',
              }}
            >
              <option value="all">All</option>
              <option value="deposits">Deposits</option>
              <option value="withdrawals">Withdrawals</option>
              <option value="auto-withdrawals">Auto-withdrawals</option>
              <option value="raffles">Raffles</option>
            </select>
          </div>
        </div>
        {latest.length === 0 ? (
          <p className="muted">
            {selectedStatus || selectedCurrency
              ? `No ${selectedType === 'all' ? 'transactions' : selectedType === 'withdrawals' ? 'withdrawals' : selectedType === 'auto-withdrawals' ? 'auto-withdrawals' : selectedType === 'raffles' ? 'raffles' : 'deposits'} match the selected filters.`
              : selectedType === 'all' ? 'No transactions yet.' : selectedType === 'withdrawals' ? 'No withdrawals yet.' : selectedType === 'auto-withdrawals' ? 'No auto-withdrawals yet.' : selectedType === 'raffles' ? 'No raffles yet.' : 'Create your first payment with the Telegram bot to see it here.'}
          </p>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    {selectedType === 'all' ? (
                      <>
                        <th>Type</th>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Currency</th>
                        <th>Amount</th>
                        <th>Created</th>
                        <th>Payer</th>
                      </>
                    ) : selectedType === 'withdrawals' ? (
                      <>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Address</th>
                        <th>Amount</th>
                        <th>Created</th>
                        <th>Tx ID</th>
                      </>
                    ) : selectedType === 'auto-withdrawals' ? (
                      <>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Merchant Address</th>
                        <th>Amount</th>
                        <th>Created</th>
                        <th>Tx ID</th>
                      </>
                    ) : selectedType === 'raffles' ? (
                      <>
                        <th>Raffle ID</th>
                        <th>Status</th>
                        <th>Prize</th>
                        <th>Participants</th>
                        <th>Scheduled</th>
                        <th>Created</th>
                      </>
                    ) : (
                      <>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Currency</th>
                        <th>Amount</th>
                        <th>Created</th>
                        <th>Expires</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {latest.map(item => {
                    if (selectedType === 'all') {
                      // Combined view for all transactions
                      const getTypeBadgeColor = () => TRANSACTION_TYPE_COLORS[item.type] || DEFAULT_TYPE_COLOR;
                      const getTypeLabel = () => TRANSACTION_TYPE_LABELS[item.type] || DEFAULT_TYPE_LABEL;
                      const isCompletedDeposit = item.type === 'payment' && item.status === 'completed' && item.orderId;
                      const canOpenLedger = item.type !== 'payment' && isLedgerClickable(item);
                      const rowProps = isCompletedDeposit
                        ? getClickableRowProps(item.orderId!)
                        : canOpenLedger
                          ? {
                              className: 'clickable-row',
                              onClick: () => openLedgerDetails(item),
                              onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) =>
                                handleLedgerRowKeyDown(event, item),
                              tabIndex: 0,
                              role: 'button' as const,
                            }
                          : {};
                      return (
                        <tr key={`${item.type}-${item.id || item.orderId}`} {...rowProps}>
                          <td>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: getTypeBadgeColor(),
                              borderRadius: '0.25rem',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              color: '#fff',
                            }}>
                              {getTypeLabel()}
                            </span>
                          </td>
                          <td>
                            {item.type === 'withdrawal' ? (
                              <span className={canOpenLedger ? 'order-link-text' : undefined}>{item.id}</span>
                            ) : item.type === 'raffle_create' || item.type === 'raffle_win' || item.type === 'raffle_refund' || item.type === 'correction' || item.type === 'refund' ? (
                              <span style={{ fontSize: '0.9rem' }}>{(item as any).description || '—'}</span>
                            ) : (
                              item.orderId
                                ? <span className={isCompletedDeposit || canOpenLedger ? 'order-link-text' : undefined}>{item.orderId}</span>
                                : '—'
                            )
                            }
                          </td>
                          <td>
                            <span className={statusColors[item.status] ?? 'status-badge'}>
                              {formatStatus(item.status)}
                            </span>
                          </td>
                          <td>{item.currency || 'USDT'}</td>
                          <td>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: item.type === 'withdrawal' || item.type === 'auto-withdrawal' ? 2 : 8 })} {item.currency || 'USDT'}</td>
                          <td>{formatDateTime(item.createdAt)}</td>
                          <td>
                            {item.type === 'payment' ? (
                              item.isAnonymous === false && (item.payerUsername || item.payerFirstName || item.payerTelegramId || item.payerEmail || item.payerPhone) ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  {/* Telegram */}
                                  {item.payerUsername ? (
                                    <a
                                      href={`https://t.me/${item.payerUsername}`}
                                      style={{ color: '#3b82f6', textDecoration: 'none' }}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      @{item.payerUsername}
                                    </a>
                                  ) : item.payerFirstName || item.payerTelegramId ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                      <span>{item.payerFirstName || 'User'}</span>
                                      {item.payerTelegramId && (
                                        <button
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: '#6b7280', fontSize: '11px', fontFamily: 'monospace' }}
                                          title="Copy Telegram ID"
                                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(item.payerTelegramId!); }}
                                        >
                                          #{item.payerTelegramId}
                                        </button>
                                      )}
                                    </span>
                                  ) : null}
                                  {/* Email */}
                                  {item.payerEmail && (
                                    <a
                                      href={`mailto:${item.payerEmail}`}
                                      style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '12px' }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      ✉ {item.payerEmail}
                                    </a>
                                  )}
                                  {/* Phone */}
                                  {item.payerPhone && (
                                    <a
                                      href={`tel:${item.payerPhone}`}
                                      style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '12px' }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      📞 {item.payerPhone}
                                    </a>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>Anonymous</span>
                              )
                            ) : (
                              <span style={{ color: '#666' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    } else if (item.type === 'auto-withdrawal') {
                      // Auto-withdrawal (merchant payout)
                      const rowProps = isLedgerClickable(item)
                        ? {
                            className: 'clickable-row',
                            onClick: () => openLedgerDetails(item),
                            onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) =>
                              handleLedgerRowKeyDown(event, item),
                            tabIndex: 0,
                            role: 'button' as const,
                          }
                        : {};
                      return (
                        <tr key={item.id} {...rowProps}>
                          <td>
                            {item.orderId ? <span className="order-link-text">{item.orderId}</span> : '—'}
                          </td>
                          <td>
                            <span className={statusColors[item.status] ?? 'status-badge'}>
                              {formatStatus(item.status)}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{item.merchantAddress}</td>
                          <td>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</td>
                          <td>{formatDateTime(item.createdAt)}</td>
                          <td style={{ fontSize: '0.85rem', color: item.txId ? '#fff' : '#999' }}>
                            {item.txId || '—'}
                          </td>
                        </tr>
                      );
                    } else if (item.type === 'withdrawal') {
                      const rowProps = isLedgerClickable(item)
                        ? {
                            className: 'clickable-row',
                            onClick: () => openLedgerDetails(item),
                            onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) =>
                              handleLedgerRowKeyDown(event, item),
                            tabIndex: 0,
                            role: 'button' as const,
                          }
                        : {};
                      return (
                         <tr key={item.id} {...rowProps}>
                          <td>{item.id}</td>
                          <td>
                            <span className={statusColors[item.status] ?? 'status-badge'}>
                              {formatStatus(item.status)}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{item.withdrawalAddress}</td>
                          <td>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</td>
                          <td>{formatDateTime(item.createdAt)}</td>
                          <td style={{ fontSize: '0.85rem', color: item.txId ? '#fff' : '#999' }}>
                            {item.txId || '—'}
                          </td>
                        </tr>
                      );
                    } else if (item.type === 'raffle') {
                      // Raffle
                      return (
                        <tr key={item.id}>
                          <td>
                            <span style={{ fontFamily: 'monospace' }}>{item.raffleId}</span>
                          </td>
                          <td>
                            <span className={statusColors[item.status] ?? 'status-badge'}>
                              {formatStatus(item.status)}
                            </span>
                          </td>
                          <td>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</td>
                          <td>{item.participantCount ?? 0}</td>
                          <td>{formatDateTime(item.scheduledAt!)}</td>
                          <td>{formatDateTime(item.createdAt)}</td>
                        </tr>
                      );
                    } else {
                      // Payment order
                      const rowProps =
                        item.status === 'completed' && item.orderId
                          ? getClickableRowProps(item.orderId)
                          : {};

                      return (
                        <tr key={item.orderId} {...rowProps}>
                          <td>
                            <span className={item.status === 'completed' ? 'order-link-text' : undefined}>
                              {item.orderId}
                            </span>
                          </td>
                          <td>
                            <span className={statusColors[item.status] ?? 'status-badge'}>
                              {formatStatus(item.status)}
                            </span>
                          </td>
                          <td>{item.currency}</td>
                          <td>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })} {item.currency}</td>
                          <td>{formatDateTime(item.createdAt)}</td>
                          <td>{formatDateTime(item.expiresAt)}</td>
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #333' }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: '0.5rem 1rem', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                ← Previous
              </button>
              <span style={{ fontSize: '0.9rem', color: '#999' }}>
                Showing {latest.length} {selectedType === 'all' ? 'transactions' : selectedType === 'withdrawals' ? 'withdrawals' : selectedType === 'auto-withdrawals' ? 'auto-withdrawals' : selectedType === 'raffles' ? 'raffles' : 'deposits'}
              </span>
              <button
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={latest.length < ORDERS_PER_PAGE}
                style={{ padding: '0.5rem 1rem', cursor: latest.length < ORDERS_PER_PAGE ? 'not-allowed' : 'pointer', opacity: latest.length < ORDERS_PER_PAGE ? 0.5 : 1 }}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeWithdrawalModal}
        >
          <div
            style={{
              backgroundColor: '#0f1419',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid #1e293b',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {withdrawSuccess ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '2rem', marginBottom: '1rem' }}>✓</p>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#10b981' }}>Withdrawal Submitted</h3>
                <p style={{ margin: 0, color: '#999' }}>Your withdrawal request has been submitted for processing.</p>
              </div>
            ) : (
              <>
                <h2 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem' }}>Withdraw Balance</h2>

                {state.kind === 'loaded' && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#1a1f2e', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#999', fontSize: '0.9rem' }}>Available Balance</p>
                    <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#10b981' }}>
                      {state.balance.balanceUsdt.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      USDT
                    </p>
                  </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#999', fontSize: '0.9rem' }}>
                    Withdrawal Amount (USDT)
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    disabled={withdrawLoading}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: '#1a1f2e',
                      color: '#fff',
                      border: '1px solid #333',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      boxSizing: 'border-box',
                      opacity: withdrawLoading ? 0.6 : 1,
                      cursor: withdrawLoading ? 'not-allowed' : 'text',
                    }}
                  />
                  {state.kind === 'loaded' && withdrawAmount && parseFloat(withdrawAmount) > state.balance.balanceUsdt && (
                    <p style={{ margin: '0.5rem 0 0 0', color: '#ef4444', fontSize: '0.85rem' }}>Amount exceeds available balance</p>
                  )}
                </div>

                {savedWithdrawalAddresses.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#999', fontSize: '0.9rem' }}>
                      Recently Used Addresses
                    </label>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          setWithdrawAddress(e.target.value);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        backgroundColor: '#1a1f2e',
                        color: '#fff',
                        border: '1px solid #333',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="">-- Select from saved addresses --</option>
                      {savedWithdrawalAddresses.map((addr, idx) => (
                        <option key={idx} value={addr}>
                          {addr.substring(0, 10)}...{addr.substring(addr.length - 4)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#999', fontSize: '0.9rem' }}>
                    TRON Wallet Address
                  </label>
                  <input
                    type="text"
                    placeholder="T..."
                    value={withdrawAddress}
                    onChange={(e) => setWithdrawAddress(e.target.value)}
                    disabled={withdrawLoading}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: '#1a1f2e',
                      color: '#fff',
                      border: '1px solid #333',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      boxSizing: 'border-box',
                      opacity: withdrawLoading ? 0.6 : 1,
                      cursor: withdrawLoading ? 'not-allowed' : 'text',
                    }}
                  />
                  {withdrawAddress && !/^T[1-9A-HJ-NP-Za-km-np-z]{33}$/.test(withdrawAddress.trim()) && (
                    <p style={{ margin: '0.5rem 0 0 0', color: '#ef4444', fontSize: '0.85rem' }}>Invalid TRON address format</p>
                  )}
                </div>

                {withdrawError && (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#7f1d1d', borderRadius: '0.5rem', border: '1px solid #991b1b' }}>
                    <p style={{ margin: 0, color: '#fca5a5', fontSize: '0.9rem' }}>{withdrawError}</p>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={handleWithdrawalSubmit}
                    disabled={withdrawLoading || !withdrawAmount || !withdrawAddress}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      backgroundColor: '#f97316',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: withdrawLoading || !withdrawAmount || !withdrawAddress ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      opacity: withdrawLoading || !withdrawAmount || !withdrawAddress ? 0.5 : 1,
                    }}
                  >
                    {withdrawLoading ? 'Processing...' : 'Withdraw'}
                  </button>
                  <button
                    onClick={closeWithdrawalModal}
                    disabled={withdrawLoading}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      backgroundColor: '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: withdrawLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      opacity: withdrawLoading ? 0.5 : 1,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showOrderDetailsModal && (
        <div className="details-modal-overlay" onClick={closeOrderDetails}>
          <div className="details-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close-button"
              aria-label="Close order details"
              onClick={closeOrderDetails}
            >
              ×
            </button>

            {orderDetailsLoading ? (
              <div className="details-loading">
                <p>Loading order details...</p>
              </div>
            ) : orderDetailsError ? (
              <div className="details-error">
                <h3>Unable to load details</h3>
                <p>{orderDetailsError}</p>
                <div className="details-error-actions">
                  <button type="button" className="primary" onClick={retryOrderDetails}>
                    Try again
                  </button>
                  <button type="button" onClick={closeOrderDetails}>
                    Close
                  </button>
                </div>
              </div>
            ) : orderDetails ? (
              <>
                <div className="details-header">
                  <div>
                    <p className="details-label">Order ID</p>
                    <h2>#{orderDetails.orderId}</h2>
                  </div>
                  <span className={`status-badge ${orderDetails.status}`}>
                    {formatStatus(orderDetails.status)}
                  </span>
                </div>
                <p className="muted" style={{ marginBottom: '1rem' }}>
                  Created {formatDateTime(orderDetails.createdAt)} · Completed{' '}
                  {formatDateTime(orderDetails.confirmedAt)}
                </p>

                <div className="details-grid">
                  <div className="details-section">
                    <h3>Amounts</h3>
                    <div className="details-row">
                      <span className="details-label">Customer paid</span>
                      <span className="details-value">
                        {formatCurrency(orderDetails.cryptoAmount, orderDetails.currency)}
                      </span>
                    </div>
                    <div className="details-row">
                      <span className="details-label">Charged (USDT)</span>
                      <span className="details-value">
                        {formatCurrency(orderDetails.userAmountUsdt, 'USDT')}
                      </span>
                    </div>
                    <div className="details-row">
                      <span className="details-label">Net credited</span>
                      <span className="details-value">
                        {formatCurrency(orderDetails.netAmountUsdt, 'USDT')}
                      </span>
                    </div>
                  </div>

                  <div className="details-section">
                    <h3>Fees</h3>
                    <div className="details-row">
                      <span className="details-label">Service fee</span>
                      <span className="details-value">
                        {formatCurrency(orderDetails.serviceFeeUsdt, 'USDT')}
                      </span>
                    </div>
                    <div className="details-row">
                      <span className="details-label">Network fees</span>
                      <span className="details-value">
                        {formatCurrency(orderDetails.transferFeeUsdt, 'USDT')}
                      </span>
                    </div>
                    <div className="details-row">
                      <span className="details-label">Total fees</span>
                      <span className="details-value">
                        {formatCurrency(detailsFeeTotalUsdt, 'USDT')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="details-section details-section-full">
                  <h3>Payment routing</h3>
                  <div className="details-row">
                    <span className="details-label">Deposit address</span>
                    <span className="details-hash">{orderDetails.depositAddress}</span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">Destination</span>
                    <span className="details-value">
                      {orderDetails.merchantWallet
                        ? `Direct payout to ${orderDetails.merchantWallet}`
                        : 'Credited to dashboard balance'}
                    </span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">Transaction hash</span>
                    {primaryTxHash ? (
                      <a
                        href={`https://tronscan.org/#/transaction/${primaryTxHash}`}
                        target="_blank"
                        rel="noreferrer"
                        className="details-hash"
                      >
                        {primaryTxHash}
                      </a>
                    ) : (
                      <span className="muted">Awaiting confirmation</span>
                    )}
                  </div>
                  <div className="details-row">
                    <span className="details-label">Confirmed</span>
                    <span className="details-value">
                      {formatDateTime(orderDetails.confirmedAt)}
                    </span>
                  </div>
                </div>

                {orderDetails.merchantWallet && (
                  <div className="details-section">
                    <h3>Merchant payout</h3>
                    <div className="details-row">
                      <span className="details-label">Wallet</span>
                      <span className="details-hash">{orderDetails.merchantWallet}</span>
                    </div>
                    <div className="details-row">
                      <span className="details-label">Status</span>
                      <span className="details-value">
                        {orderDetails.merchantPayout
                          ? formatStatus(orderDetails.merchantPayout.status)
                          : 'Pending'}
                      </span>
                    </div>
                    {orderDetails.merchantPayout?.txId && (
                      <div className="details-row">
                        <span className="details-label">Transfer hash</span>
                        <span className="details-hash">{orderDetails.merchantPayout.txId}</span>
                      </div>
                    )}
                  </div>
                )}

                {orderDetails.transactions.length > 0 && (
                  <div className="details-section details-section-full">
                    <h3>Network records</h3>
                    <div className="details-transaction-list">
                      {orderDetails.transactions.map(tx => (
                        <div key={tx.id} className="details-transaction-row">
                          <div>
                            <p className="details-value">
                              {formatCurrency(tx.amount, tx.currency)}
                            </p>
                            <p className="details-subtext">{formatDateTime(tx.detectedAt)}</p>
                          </div>
                          <div className="details-transaction-meta">
                            <span className="details-chip">{formatStatus(tx.status)}</span>
                            <span className="details-hash">{tx.txId}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="details-actions">
                  {primaryTxHash && (
                    <a
                      className="primary"
                      href={`https://tronscan.org/#/transaction/${primaryTxHash}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in TronScan
                    </a>
                  )}
                  <button type="button" onClick={closeOrderDetails}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <div className="details-loading">
                <p>Order details unavailable.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showLedgerDetailsModal && ledgerDetails && (
        <div className="details-modal-overlay" onClick={closeLedgerDetails}>
          <div className="details-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close-button"
              aria-label="Close ledger details"
              onClick={closeLedgerDetails}
            >
              ×
            </button>
            <div className="details-header">
              <div>
                <p className="details-label">
                  {ledgerDetails.type === 'withdrawal'
                    ? 'Withdrawal request'
                    : 'Auto payout'}
                </p>
                <h2>
                  {ledgerDetails.type === 'withdrawal'
                    ? `#${ledgerDetails.id}`
                    : ledgerDetails.orderId
                      ? `Order ${ledgerDetails.orderId}`
                      : ledgerDetails.id}
                </h2>
              </div>
              <span className={statusColors[ledgerDetails.status] ?? 'status-badge'}>
                {formatStatus(ledgerDetails.status)}
              </span>
            </div>
            <div className="details-grid">
              <div className="details-section">
                <h3>Amounts</h3>
                <div className="details-row">
                  <span className="details-label">Amount</span>
                  <span className="details-value">
                    {formatCurrency(ledgerDetails.amount, 'USDT')}
                  </span>
                </div>
                <div className="details-row">
                  <span className="details-label">Created</span>
                  <span className="details-value">{formatDateTime(ledgerDetails.createdAt)}</span>
                </div>
                {ledgerDetails.sentAt && (
                  <div className="details-row">
                    <span className="details-label">Sent</span>
                    <span className="details-value">{formatDateTime(ledgerDetails.sentAt)}</span>
                  </div>
                )}
              </div>

              <div className="details-section">
                <h3>Destination</h3>
                <div className="details-row">
                  <span className="details-label">
                    {ledgerDetails.type === 'withdrawal' ? 'Wallet address' : 'Merchant wallet'}
                  </span>
                  <span className="details-hash">
                    {ledgerDetails.type === 'withdrawal'
                      ? ledgerDetails.withdrawalAddress
                      : ledgerDetails.merchantAddress}
                  </span>
                </div>
                <div className="details-row">
                  <span className="details-label">Transaction hash</span>
                  {ledgerDetails.txId ? (
                    <a
                      href={`https://tronscan.org/#/transaction/${ledgerDetails.txId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="details-hash"
                    >
                      {ledgerDetails.txId}
                    </a>
                  ) : (
                    <span className="muted">Awaiting transfer</span>
                  )}
                </div>
              </div>

              {/* Error section for failed payouts */}
              {ledgerDetails.status === 'failed' && ledgerDetails.lastError && (
                <div className="details-section error-section">
                  <h3>Error Details</h3>
                  <div className="error-message">
                    {ledgerDetails.lastError}
                  </div>
                  {ledgerDetails.attempts !== undefined && (
                    <div className="details-row">
                      <span className="details-label">Attempts</span>
                      <span className="details-value">{ledgerDetails.attempts}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {retryError && (
              <div className="error-banner">{retryError}</div>
            )}

            <div className="details-actions">
              {ledgerDetails.txId && (
                <a
                  className="primary"
                  href={`https://tronscan.org/#/transaction/${ledgerDetails.txId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on TronScan
                </a>
              )}
              {/* Retry button for failed auto-withdrawals (admin only) */}
              {ledgerDetails.type === 'auto-withdrawal' &&
                ledgerDetails.status === 'failed' &&
                user?.isAdmin && (
                  <button
                    type="button"
                    className="primary"
                    onClick={handleRetryPayout}
                    disabled={retryLoading}
                  >
                    {retryLoading ? 'Retrying...' : 'Retry Payout'}
                  </button>
                )}
              <button type="button" onClick={closeLedgerDetails}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DashboardStatProps {
  title: string;
  value: string;
  tone?: 'green' | 'amber' | 'slate';
  onClick?: () => void;
}

function DashboardStat({ title, value, tone = 'green', onClick }: DashboardStatProps) {
  return (
    <div
      className={`card metric metric-${tone}${onClick ? ' metric-clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <span className="metric-title">{title}</span>
      <span className="metric-value">{value}</span>
    </div>
  );
}

export default DashboardPage;
